export const NETWORKS = [
  { value: "visa", label: "Visa" },
  { value: "mastercard", label: "Mastercard" },
  { value: "amex", label: "Amex" },
  { value: "jcb", label: "JCB" },
  { value: "unionpay", label: "UnionPay" },
  { value: "discover", label: "Discover" },
  { value: "other", label: "Other" },
] as const;

export const CARD_TYPES = [
  { value: "credit", label: "Credit" },
  { value: "debit", label: "Debit" },
  { value: "prepaid", label: "Prepaid" },
  { value: "membership", label: "Membership" },
  { value: "charge", label: "Charge" },
  { value: "virtual", label: "Virtual" },
  { value: "other", label: "Other" },
] as const;

export const TIERS = [
  { value: "none", label: "None (generic / undefined)" },
  { value: "debit", label: "Debit" },
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
  { value: "elite", label: "Elite" },
] as const;

export type TierOption = { value: string; label: string };

/** Shared first options: unset/generic, then Debit (applies to credit and debit products). */
const SHARED_TIER_PREFIX: TierOption[] = [
  { value: "none", label: "None (generic / undefined)" },
  { value: "debit", label: "Debit" },
];

/**
 * Published consumer tiers per card network, ordered entry-level first.
 * Keys are lowercased network slugs; `unionPay` is matched case-insensitively.
 * Every list starts with None + Debit so debit products and undefined tiers are covered.
 */
export const TIERS_BY_NETWORK: Record<string, TierOption[]> = {
  visa: [
    ...SHARED_TIER_PREFIX,
    { value: "classic", label: "Classic" },
    { value: "gold", label: "Gold" },
    { value: "platinum", label: "Platinum" },
    { value: "signature", label: "Signature" },
    { value: "infinite", label: "Infinite" },
    { value: "infinite-privilege", label: "Infinite Privilege" },
    { value: "business", label: "Business" },
  ],
  mastercard: [
    ...SHARED_TIER_PREFIX,
    { value: "standard", label: "Standard" },
    { value: "gold", label: "Gold" },
    { value: "titanium", label: "Titanium" },
    { value: "platinum", label: "Platinum" },
    { value: "world", label: "World" },
    { value: "world-elite", label: "World Elite" },
    { value: "business", label: "Business" },
  ],
  amex: [
    ...SHARED_TIER_PREFIX,
    { value: "blue", label: "Blue" },
    { value: "green", label: "Green" },
    { value: "gold", label: "Gold" },
    { value: "rose-gold", label: "Rose Gold" },
    { value: "platinum", label: "Platinum" },
    { value: "centurion", label: "Centurion (Black)" },
  ],
  jcb: [
    ...SHARED_TIER_PREFIX,
    { value: "standard", label: "Standard" },
    { value: "gold", label: "Gold" },
    { value: "gold-premier", label: "Gold Premier" },
    { value: "platinum", label: "Platinum" },
    { value: "precious-metal", label: "Precious Metal" },
    { value: "the-class", label: "The Class" },
  ],
  unionpay: [
    ...SHARED_TIER_PREFIX,
    { value: "classic", label: "Classic" },
    { value: "gold", label: "Gold" },
    { value: "platinum", label: "Platinum" },
    { value: "diamond", label: "Diamond" },
    { value: "infinite", label: "Infinite" },
  ],
};

/** Falls back to the generic ladder for Discover / other / unmapped networks. */
export function tiersForNetwork(networkSlug: string | null | undefined): TierOption[] {
  const key = (networkSlug ?? "").trim().toLowerCase();
  return TIERS_BY_NETWORK[key] ?? [...TIERS];
}

export const ACCEPTED_IMAGE_TYPES = [
  "image/svg+xml",
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

export const ACCEPTED_IMAGE_EXTENSIONS = ".svg,.png,.jpg,.jpeg,.webp";

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export type Network = (typeof NETWORKS)[number]["value"];
export type CardType = (typeof CARD_TYPES)[number]["value"];
export type Tier = (typeof TIERS)[number]["value"];

// Ink & Brass placeholder; real products override with issuer colors.
export function defaultGradientJson(start = "#1A181A", end = "#8C6B33"): string {
  return JSON.stringify([start, end]);
}

export function parseGradientColors(json: string): [string, string] {
  try {
    const parsed = JSON.parse(json) as unknown;
    if (Array.isArray(parsed) && parsed.length >= 2) {
      return [String(parsed[0]), String(parsed[1])];
    }
  } catch {
    // fall through
  }
  return ["#1A181A", "#8C6B33"];
}
